import React, {useEffect, useState} from 'react';
import {
  Grid,
  TextField, FormControl, Checkbox, FormControlLabel, Box, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography,
} from '@material-ui/core';
import {DeleteOutlined, InsertLink} from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import SingleAutoComplete from "../../shared/SingleAutoComplete";
import AddressesSection from "../../addressMatching/form/AddressesSection";
import PrivateWealthTeamsService from "../../services/PrivateWealthTeamsService";
import TextInput from "../../sharedFormComponents/TextInput";
import {useFieldWithValidation} from "../../../../hooks";
import {validPhoneNumber} from "../../../../helpers/utils";
import useAdminUserSession from "../../../../hooks/useAdminUserSession";
import {VALIDATED_BY_FINTRX, isNoMatchSummary} from "../../../../helpers/addressValidationSummary";
import {CopyToClipboard} from "react-copy-to-clipboard/src";
import CustomSnackbar from "../../../user/shared/CustomSnackbar";

const useStyles = makeStyles((theme) => ({
  root: {},
  fieldsRow: {
    marginBottom: '12px',
  },
  removeLocation: {
    padding: '16px 0',
    marginBottom: '8px',
    cursor: "pointer"
  },
  validateBtn: {
    height: 56,
    fontWeight: 700,
    fontSize: "clamp(7px, 0.7vw, 12px)",
    letterSpacing: 0.2,
    padding: "6px 8px",
    minWidth: 0,
    lineHeight: 1.2,
    whiteSpace: "normal",
    wordBreak: "break-word",
    backgroundColor: "#2e7d32",
    color: "#fff",
    "&:hover": { backgroundColor: "#1b5e20" },
    "&:disabled": { backgroundColor: undefined },
  },
  validatedBtn: {
    height: 56,
    fontWeight: 700,
    fontSize: "clamp(7px, 0.7vw, 12px)",
    letterSpacing: 0.2,
    padding: "6px 8px",
    minWidth: 0,
    lineHeight: 1.2,
    whiteSpace: "normal",
    wordBreak: "break-word",
    backgroundColor: "#f1f8f1",
    color: "#2e7d32",
    border: "1px solid #a5d6a7",
    boxShadow: "none",
    pointerEvents: "none",
    "&:hover": { backgroundColor: "#f1f8f1", boxShadow: "none" },
  },
}))

const buildAddressLines = (fields = {}) => ({
  addressLine1: [
    fields.primary_number,
    fields.street_predirection,
    fields.street_name,
    fields.street_suffix,
    fields.street_postdirection,
  ].filter(Boolean).join(" "),
  addressLine2: [
    fields.secondary_designator,
    fields.secondary_number,
    fields.extra_secondary_designator,
    fields.extra_secondary_number,
  ].filter(Boolean).join(" "),
});

const TeamLocationsFields = ({
                               location,
                               locationOptions,
                               handleNestedFieldChange,
                               handleRemoveNestedFields,
                               isPrincipal,
                               handleSetPrincipalLocation,
                               showRemoveLocation = true,
                               onRemoveLocation,
                               ...props
                             }) => {
  const classes = useStyles();

  const [snackBarMessage, setSnackBarMessage] = useState({show: false, message: '', severity: 'success'})
  const [validationResults, setValidationResults] = useState(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const {reset: resetPhoneNumber, validate: validatePhoneNumber, ...phone_number} = useFieldWithValidation(
      "text",
      location.phone_number || "",
      (value) => (validPhoneNumber(value)),
      false,
      ({target:{ name, value }}) => handleOnChangeAutocomplete(name, value),
      (value) => (value && !validPhoneNumber(value)) ? "Invalid Phone number." : null
  );
  const {session} = useAdminUserSession();

  const handleOnChangeAutocomplete = (field, selectedValue) => {
    handleNestedFieldChange('locations', location, field, selectedValue);
  }

  useEffect(() => {
    const validation = location.pwt_location_validation;
    if (!validation) return;

    const {addressLine1, addressLine2} = buildAddressLines(validation);
    const currentCityName = locationOptions.cities.find((city) => city.id === location.city_id)?.name;
    const stillMatches =
        validation.summary !== 'Flagged' && !isNoMatchSummary(validation.summary) &&
        validation.full_zipcode === location.zip_code &&
        validation.state_abbreviation === location.state &&
        (validation.city_name || '').toLowerCase() === (currentCityName || '').toLowerCase() &&
        addressLine1 === (location.address || '') &&
        addressLine2 === (location.address_2 || '');
    if (stillMatches && location.address_validated === undefined) {
      handleOnChangeAutocomplete('address_validated', true);
    }
  }, [location.id, location.pwt_location_validation, location.address, location.address_2, location.state, location.zip_code, location.city_id, locationOptions.cities]);

  const handleCountryChange = (country) => {
    if (country) {
      if (locationOptions.unitedStatesId === country.id) {
        handleOnChangeAutocomplete('state', null)
        handleOnChangeAutocomplete('state_id', null)
        handleOnChangeAutocomplete('city', null)
        handleOnChangeAutocomplete('city_id', null)
      }
      handleOnChangeAutocomplete('country', country.name)
      handleOnChangeAutocomplete('country_id', country.id)
    }else {
      handleOnChangeAutocomplete('country', '')
      handleOnChangeAutocomplete('country_id', null)
    }
    handleOnChangeAutocomplete('address_validated', false)
  }

  const handleCheckChange = (event) => {
    event.persist();

    const isChecked = event.target.checked;
    const locationIndex = props.index;

    handleSetPrincipalLocation(locationIndex, isChecked);
  }

  const handleIgnoreCheckChange = (event) => {
    event.persist();

    const isChecked = event.target.checked;
    handleOnChangeAutocomplete('ignore_validation', isChecked)
  }

  const handleStateChange = (state) => {
    handleOnChangeAutocomplete('state', state ? state.id : null)
    handleOnChangeAutocomplete('address_validated', false)
  }

  const handleCityChange = (city) => {
    handleOnChangeAutocomplete('city', city ? city.name : '')
    handleOnChangeAutocomplete('city_id', city ? city.id : null)
    handleOnChangeAutocomplete('address_validated', false)
  }

  const handleOnChange = (event) => {
    event.persist();

    handleOnChangeAutocomplete(event.target.name, event.target.value)
    if (['address', 'address_2', 'zip_code'].includes(event.target.name)) {
      handleOnChangeAutocomplete('address_validated', false)
    }
  }

  const onCopy = () => {
    setSnackBarMessage({show: true, message: 'Address Copied', severity: 'success'})
  }

  const handleCloseSnackbar = () => {
    setSnackBarMessage({show: false, message: '', severity: 'success'})
  }

  // Smarty's US Street API only validates US addresses; non-US locations get a manual
  // confirm step instead, same as the Bank & Trust firm location flow this is modeled on.
  const isUS = !location.country_id || !locationOptions.unitedStatesId || location.country_id === locationOptions.unitedStatesId;
  const hasStreet = !!location.address?.trim()?.length;
  const isValidated = location.address_validated === true;
  const validateButtonDisabled = validating || !location.country_id || (!isValidated && !hasStreet);
  const validateButtonClass = isValidated ? classes.validatedBtn : classes.validateBtn;
  const validateButtonText = validating
      ? 'Validating...'
      : isValidated
          ? (isUS ? '✓ VALIDATED' : '✓ CONFIRMED')
          : (isUS ? 'VALIDATE' : 'CONFIRM');

  const handleValidateAddress = () => {
    setValidating(true);
    PrivateWealthTeamsService.validateAddress(session, {
      street_1: location.address,
      street_2: location.address_2,
      zip_code: location.zip_code,
      state: location.state,
      city: locationOptions.cities.find((city) => city.id === location.city_id)?.name,
    }).then((response) => {
      const { status, addresses, message } = response.data;
      if (status === 'success') {
        setValidationResults(
            addresses.address_validations.map((address) => ({
              ...address,
              isSelected: false,
            }))
        );
        setValidationDialogOpen(true);
      } else {
        setSnackBarMessage({show: true, message: message || 'Address could not be validated', severity: 'error'})
      }
    }).catch((error) => {
      const message = error.response?.data?.message || 'Address could not be validated';
      setSnackBarMessage({show: true, message, severity: 'error'})
    }).finally(() => {
      setValidating(false);
    });
  };

  const handleConfirmNonUsAddress = () => {
    handleOnChangeAutocomplete('pwt_location_validation_attributes', {
      ...(location.pwt_location_validation?.id ? {id: location.pwt_location_validation.id} : {}),
      street_name: location.address,
      secondary_number: location.address_2,
      city_name: locationOptions.cities.find((city) => city.id === location.city_id)?.name,
      state_abbreviation: location.state,
      zipcode: location.zip_code,
      full_zipcode: location.zip_code,
      summary: VALIDATED_BY_FINTRX,
    });
    handleOnChangeAutocomplete('validation_type', 'Manual');
    handleOnChangeAutocomplete('address_validated', true);
  };

  const handleValidateButtonClick = isValidated
      ? undefined
      : (isUS ? handleValidateAddress : handleConfirmNonUsAddress);

  const handleValidationSelect = (fieldName, updatedAddresses) => {
    if (fieldName === "addresses") {
      setValidationResults(updatedAddresses);
    }
  };

  const handleAcceptValidation = () => {
    const selectedAddress = validationResults?.find((address) => address.isSelected);
    if (!selectedAddress) return;

    if (isNoMatchSummary(selectedAddress.summary)) {
      setSnackBarMessage({
        show: true,
        message: `Smarty could not confirm this address ("${selectedAddress.summary}") -- it cannot be accepted as validated.`,
        severity: 'error',
      });
      return;
    }

    const {isSelected, ...candidateAttributes} = selectedAddress;
    const {addressLine1, addressLine2} = buildAddressLines(selectedAddress);

    const candidateCities = locationOptions.cities.filter(
        (city) => city.country_id === location.country_id &&
            city.name?.toLowerCase() === selectedAddress.city_name?.toLowerCase()
    );
    const cityMatched = candidateCities.length === 1;

    handleOnChangeAutocomplete('address', addressLine1);
    handleOnChangeAutocomplete('address_2', addressLine2);
    handleOnChangeAutocomplete('state', selectedAddress.state_abbreviation);
    if (cityMatched) {
      handleOnChangeAutocomplete('city_id', candidateCities[0].id);
    } else {
      setSnackBarMessage({
        show: true,
        message: `Could not confidently match city "${selectedAddress.city_name || ''}" -- please verify/select it manually, then validate again.`,
        severity: 'error',
      });
    }
    handleOnChangeAutocomplete('zip_code', selectedAddress.full_zipcode || location.zip_code);
    handleOnChangeAutocomplete('latitude', selectedAddress.latitude);
    handleOnChangeAutocomplete('longitude', selectedAddress.longitude);
    handleOnChangeAutocomplete('validation_type', 'Automatic');
    handleOnChangeAutocomplete('pwt_location_validation_attributes', {
      ...(location.pwt_location_validation?.id ? {id: location.pwt_location_validation.id} : {}),
      ...candidateAttributes,
    });
    handleOnChangeAutocomplete('address_validated', cityMatched);

    setValidationDialogOpen(false);
    setValidationResults(null);
  };

    const selectedCandidate = validationResults?.find((address) => address.isSelected);
    const selectedCandidateLines = buildAddressLines(selectedCandidate);

    const { id, _destroy } = location;

    return (
      <React.Fragment>
        {_destroy ? null : (
          <Grid container spacing={2} key={id} className={classes.fieldsRow}>
            <Grid item container spacing={2} xs={10}>
              <Grid item xs={2}>
                <TextInput
                    {...phone_number}
                    label={"Phone Number"}
                    name={"phone_number"}
                    maxLength={150}
                />
              </Grid>

              <Grid
                  item
                  xs={2}
              >
                <TextField
                    fullWidth
                    label='Address'
                    variant="outlined"
                    name='address'
                    inputProps={{ maxLength: 500 }}
                    value={location.address || ''}
                    onChange={handleOnChange}
                    disabled={!!location.branch_id}
                    required={!location.ignore_validation}
                />
              </Grid>

              <Grid item xs={1}>
                <TextField
                    label='Address 2'
                    variant="outlined"
                    name='address_2'
                    inputProps={{ maxLength: 40  }}
                    value={location.address_2 || ''}
                    onChange={handleOnChange}
                    disabled={!!location.branch_id}
                />
              </Grid>

              <Grid item xs={1}>
                <FormControl fullWidth>
                  <TextField
                      label="Branch Id"
                      variant="outlined"
                      name='branch_id'
                      disabled={true}
                      inputProps={{ maxLength: 10  }}
                      value={location.branch_id || ''}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={2}>
                <SingleAutoComplete
                    options={locationOptions.countries}
                    handleOnChange={(value) => handleCountryChange(value)}
                    handleOnError={() => {}}
                    selectedId={location.country_id}
                    field={'country'}
                    label={"Country"}
                    variant={"outlined"}
                    className="entity-autocomplete"
                    autoHighlight
                    required={!location.ignore_validation}
                    disabled={!!location.branch_id}
                />
              </Grid>

              {
                  location.country_id === locationOptions.unitedStatesId && <Grid item xs={2}>
                    <SingleAutoComplete
                        options={locationOptions.states}
                        handleOnChange={(value) => handleStateChange(value)}
                        handleOnError={() => {}}
                        selectedId={location.state}
                        field={'state'}
                        label={"State"}
                        variant={"outlined"}
                        className="entity-autocomplete"
                        autoHighlight
                        disabled={!!location.branch_id}
                        required={!location.ignore_validation}
                    />
                  </Grid>
              }

              <Grid item xs={2}>
                <SingleAutoComplete
                    options={locationOptions.cities}
                    handleOnChange={(value) => handleCityChange(value)}
                    handleOnError={() => {}}
                    selectedId={location.city_id}
                    field={'city'}
                    label={"City"}
                    variant={"outlined"}
                    className="entity-autocomplete"
                    autoHighlight
                    disabled={!!location.branch_id}
                    required={!location.ignore_validation}
                />
              </Grid>
            </Grid>
            <Grid item xs={2} style={{display: 'flex', flexDirection: 'column', minWidth: 0}}>
              <TextField
                  fullWidth
                  label="Zip Code"
                  variant="outlined"
                  name='zip_code'
                  inputProps={{ maxLength: 40  }}
                  value={location.zip_code || ''}
                  onChange={handleOnChange}
                  disabled={!!location.branch_id}
                  required={!location.ignore_validation}
              />
              <Box display="flex" flexWrap="wrap" style={{gap: '5px', marginTop: '5px'}}>
                <CopyToClipboard onCopy={onCopy}
                                 text={`${location?.address || ''}, ${locationOptions.cities.find(a => a.id === location?.city_id)?.name || ''}, ${locationOptions.states.find(e => e.id === location?.state)?.name || ''} ${location?.zip_code || ''}`}>
                  <Button color="primary" variant="outlined">
                    <span style={{
                      display: 'flex',
                      whiteSpace: 'nowrap',
                      textTransform: 'capitalize',
                      fontSize: '12px',
                    }}>
                      Copy Address
                      <InsertLink style={{width: '20px', margin: '-1px 0 0 3px'}}/>
                    </span>
                  </Button>
                </CopyToClipboard>
                {!location.branch_id && !location.ignore_validation && (
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={validateButtonDisabled}
                        className={validateButtonClass}
                        onClick={handleValidateButtonClick}
                    >
                      {validateButtonText}
                    </Button>
                )}
              </Box>
              <Box display="flex" alignItems="center">
                <FormControlLabel
                    control={
                      <Checkbox
                          checked={isPrincipal}
                          onChange={handleCheckChange}
                          name="principal"
                      />}
                    label="Primary"
                    style={{marginRight: "0", padding: "8px"}}
                />
                {
                  showRemoveLocation &&
                    <DeleteOutlined style={{marginLeft: "8px"}} onClick={() => onRemoveLocation(location)} />
                }
              </Box>
              <Box display="flex">
                <FormControlLabel
                    control={
                      <Checkbox
                          checked={!!location.ignore_validation}
                          onChange={handleIgnoreCheckChange}
                          name="ignore_validation"
                      />}
                    label="Skip Validation"
                    style={{marginRight: "1", paddingLeft: "8px"}}
                />
              </Box>
            </Grid>
            {snackBarMessage.show ?
                <CustomSnackbar
                    open={snackBarMessage.show}
                    closeHandler={handleCloseSnackbar}
                    message={snackBarMessage.message}
                    options={{
                      severity: snackBarMessage.severity
                    }}
                />
                : null}

            {validationDialogOpen && (
                <Dialog
                    open={validationDialogOpen}
                    onClose={() => setValidationDialogOpen(false)}
                    fullWidth
                    maxWidth={"md"}
                    className={"dialog"}
                >
                  <DialogTitle disableTypography>
                    <h4 className={"dialog-title"}>Please confirm the validated address details.</h4>
                  </DialogTitle>
                  <DialogContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="h5">Street: {selectedCandidateLines.addressLine1 || location.address}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h5">Secondary: {selectedCandidateLines.addressLine2 || location.address_2}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h5">Zip Code: {selectedCandidate?.full_zipcode || location.zip_code}</Typography>
                      </Grid>
                      <div style={{padding: '8px', width: '100%'}}>
                        <AddressesSection
                            addresses={validationResults}
                            handleOnChangeInField={handleValidationSelect}
                        />
                      </div>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setValidationDialogOpen(false)} color="secondary">
                      Cancel
                    </Button>
                    <Button onClick={handleAcceptValidation} color="primary" variant="contained">
                      Accept
                    </Button>
                  </DialogActions>
                </Dialog>
            )}
          </Grid>
        )}
      </React.Fragment>
    )
}

export default TeamLocationsFields;
